import React from 'react';
import { Layout } from '../components/Layout';
import { Link } from 'react-router-dom';

export function Home() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-white overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-100/40 rounded-full blur-[120px]" />

        <div className="relative z-10 container mx-auto px-4 max-w-6xl py-24 md:py-32">
          <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-6">
            US Equity Infrastructure
          </p>
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tight leading-[1.1] mb-6">
            Trading Infrastructure<br />
            <span className="text-gray-400">for Global Brokerages</span>
          </h1>
          <p className="text-xl text-gray-500 max-w-2xl mb-10">
            Provide your customers seamless access to US markets with 24/5 trading,
            fractional shares, and zero-commission execution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <Link
              to="/platform"
              className="bg-brand-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-brand-600 transition-all duration-200 text-center hover:scale-[1.02]"
            >
              Learn More
            </Link>
            <Link
              to="/contact"
              className="border border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold text-lg hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 text-center"
            >
              Contact Us
            </Link>
          </div>

          {/* Stats strip */}
          <div className="flex flex-wrap gap-8 md:gap-16 pt-8 border-t border-gray-200">
            <div>
              <p className="font-mono text-3xl md:text-4xl font-bold text-brand-600">&lt;1ms</p>
              <p className="text-gray-500 text-sm mt-1">Order processing</p>
            </div>
            <div>
              <p className="font-mono text-3xl md:text-4xl font-bold text-brand-600">99.9%</p>
              <p className="text-gray-500 text-sm mt-1">Uptime target</p>
            </div>
            <div>
              <p className="font-mono text-3xl md:text-4xl font-bold text-brand-600">24/5</p>
              <p className="text-gray-500 text-sm mt-1">Extended hours</p>
            </div>
            <div>
              <p className="font-mono text-3xl md:text-4xl font-bold text-brand-600">$0</p>
              <p className="text-gray-500 text-sm mt-1">Commission</p>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-20 bg-gray-50 relative">
        <div className="absolute inset-0 dot-grid opacity-20" />
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4 text-center">
            Why Choose Us
          </p>
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900 tracking-tight">
            Built for Modern Brokerages
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Card 1 */}
            <div className="bg-white border border-gray-200 p-6 rounded-xl hover:border-brand-400/50 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">24/5 Extended Hours</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Trade around the clock, aligned with global market hours
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white border border-gray-200 p-6 rounded-xl hover:border-brand-400/50 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Zero Commission</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Eliminate the 0.25% fees from legacy providers
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white border border-gray-200 p-6 rounded-xl hover:border-brand-400/50 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">API-First Integration</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                REST API, WebSocket + FIX coming soon
              </p>
            </div>

            {/* Card 4 */}
            <div className="bg-white border border-gray-200 p-6 rounded-xl hover:border-brand-400/50 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Full Market Access</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                US equities, fractional shares, OTC securities, options (roadmap)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white relative">
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4 text-center">
            Integration Flow
          </p>
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900 tracking-tight">
            How It Works
          </h2>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-0">
            {/* Step 1 */}
            <div className="flex flex-col items-center text-center min-w-[160px]">
              <div className="font-mono text-brand-600 text-xs tracking-widest uppercase mb-3">01</div>
              <div className="bg-white border border-gray-200 shadow-sm px-6 py-4 rounded-xl">
                <p className="font-semibold text-gray-900 text-sm">Your Customers</p>
              </div>
            </div>

            {/* Connector */}
            <div className="hidden md:block w-16 h-px bg-gradient-to-r from-gray-200 to-gray-200 relative">
              <div className="absolute top-1/2 right-0 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-gray-300" />
            </div>
            <div className="md:hidden h-6 w-px bg-gray-200" />

            {/* Step 2 */}
            <div className="flex flex-col items-center text-center min-w-[160px]">
              <div className="font-mono text-brand-600 text-xs tracking-widest uppercase mb-3">02</div>
              <div className="bg-white border border-gray-200 shadow-sm px-6 py-4 rounded-xl">
                <p className="font-semibold text-gray-900 text-sm">Your Platform</p>
              </div>
            </div>

            {/* Connector */}
            <div className="hidden md:block w-16 h-px bg-gradient-to-r from-gray-200 to-brand-300 relative">
              <div className="absolute top-1/2 right-0 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-brand-400" />
            </div>
            <div className="md:hidden h-6 w-px bg-gradient-to-b from-gray-200 to-brand-300" />

            {/* Step 3 - Highlighted */}
            <div className="flex flex-col items-center text-center min-w-[160px]">
              <div className="font-mono text-brand-600 text-xs tracking-widest uppercase mb-3">03</div>
              <div className="bg-brand-50 border border-brand-200 px-6 py-4 rounded-xl shadow-sm">
                <p className="font-semibold text-brand-700 text-sm">AOK Financial</p>
              </div>
            </div>

            {/* Connector */}
            <div className="hidden md:block w-16 h-px bg-gradient-to-r from-brand-300 to-gray-200 relative">
              <div className="absolute top-1/2 right-0 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-gray-300" />
            </div>
            <div className="md:hidden h-6 w-px bg-gradient-to-b from-brand-300 to-gray-200" />

            {/* Step 4 */}
            <div className="flex flex-col items-center text-center min-w-[160px]">
              <div className="font-mono text-brand-600 text-xs tracking-widest uppercase mb-3">04</div>
              <div className="bg-white border border-gray-200 shadow-sm px-6 py-4 rounded-xl">
                <p className="font-semibold text-gray-900 text-sm">US Markets</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-gray-50 relative">
        <div className="absolute inset-0 dot-grid opacity-20" />
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4 text-center">
            Comparison
          </p>
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900 tracking-tight">
            AOK Financial vs Legacy Providers
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="px-6 py-4 text-left text-sm font-mono uppercase tracking-wider text-gray-500">Feature</th>
                  <th className="px-6 py-4 text-left text-sm font-mono uppercase tracking-wider text-gray-500">Legacy Providers</th>
                  <th className="px-6 py-4 text-left text-sm font-mono uppercase tracking-wider text-brand-600">AOK Financial</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200 hover:bg-gray-100/50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900 text-sm">Trading Hours</td>
                  <td className="px-6 py-4 text-gray-500 text-sm">Limited extended</td>
                  <td className="px-6 py-4 text-brand-600 font-mono text-sm">24/5 extended hours</td>
                </tr>
                <tr className="border-b border-gray-200 hover:bg-gray-100/50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900 text-sm">Commission</td>
                  <td className="px-6 py-4 text-gray-500 text-sm">0.25% typical</td>
                  <td className="px-6 py-4 text-brand-600 font-mono text-sm">$0</td>
                </tr>
                <tr className="border-b border-gray-200 hover:bg-gray-100/50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900 text-sm">Fractional Shares</td>
                  <td className="px-6 py-4 text-gray-500 text-sm">Limited</td>
                  <td className="px-6 py-4 text-brand-600 font-mono text-sm">Yes</td>
                </tr>
                <tr className="hover:bg-gray-100/50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900 text-sm">Integration</td>
                  <td className="px-6 py-4 text-gray-500 text-sm">Legacy APIs</td>
                  <td className="px-6 py-4 text-brand-600 font-mono text-sm">Modern REST API</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center">
            <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4">
              Compliance
            </p>
            <h2 className="text-3xl font-bold mb-12 text-gray-900 tracking-tight">Regulated & Trusted</h2>
            <div className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-16">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900">FINRA Membership</p>
                  <p className="text-xs text-gray-500 font-mono">Application Pending</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900">SEC Registered</p>
                  <p className="text-xs text-gray-500 font-mono">In Progress</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900">RQD Partner</p>
                  <p className="text-xs text-gray-500 font-mono">Established</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative bg-gradient-to-r from-brand-600 to-brand-500 py-20 overflow-hidden">
        <div className="absolute inset-0 noise-texture" />
        <div className="container mx-auto px-4 max-w-4xl text-center relative z-10">
          <h2 className="text-4xl font-bold mb-4 text-white tracking-tight">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-white/80">
            Contact our team to learn how we can power your US equity offering
          </p>
          <Link
            to="/contact"
            className="bg-white text-brand-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-200 inline-block hover:scale-[1.02]"
          >
            Contact Sales
          </Link>
        </div>
      </section>
    </Layout>
  );
}
